'use client';

import { useEffect, useState, useRef } from 'react';
import { useIsHydrated } from '@/hooks/useIsHydrated';
import ProductCard from './ProductCard';
import InquiryForm from './InquiryForm';
import Reveal from '@/components/common/Reveal';
import Icon from '@/components/ui/AppIcon';
import type { Product, ProductType } from '../productsData';
import { PRODUCT_TYPES, CATEGORY_TO_TYPE } from '@/lib/products/taxonomy';

type MaterialFilter = 'all' | 'bagasse' | 'areca';
type TypeFilter = 'all' | ProductType;

const MATERIAL_OPTIONS: { value: 'bagasse' | 'areca'; label: string }[] = [
  { value: 'bagasse', label: 'Bagasse' },
  { value: 'areca', label: 'Areca' },
];

const TYPE_ORDER = PRODUCT_TYPES;
const TYPE_SET = new Set<string>(TYPE_ORDER);

// Read the ?material= / ?type= (or legacy ?category=) deep-links on the client,
// so the products page stays statically rendered / ISR-cached.
const materialFromUrl = (): MaterialFilter => {
  if (typeof window === 'undefined') return 'all';
  const m = new URLSearchParams(window.location.search).get('material');
  return m === 'bagasse' || m === 'areca' ? m : 'all';
};

const typeFromUrl = (): TypeFilter => {
  if (typeof window === 'undefined') return 'all';
  const params = new URLSearchParams(window.location.search);
  const t = params.get('type');
  if (t && TYPE_SET.has(t)) return t as ProductType;
  const c = params.get('category');
  return c && CATEGORY_TO_TYPE[c] ? CATEGORY_TO_TYPE[c] : 'all';
};

const productFromUrl = (list: Product[]): Product | null => {
  if (typeof window === 'undefined') return null;
  const slug = new URLSearchParams(window.location.search).get('product');
  return slug ? (list.find((p) => p.id === slug) ?? null) : null;
};

const inquiryFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const t = new URLSearchParams(window.location.search).get('inquiry');
  return t === 'Sample' || t === 'Quote' || t === 'Custom' || t === 'Catalogue' ? t : null;
};

// Normalise a search query the way the design does: "9 inch" -> 9", no space.
const normaliseQuery = (q: string) =>
  q.trim().toLowerCase().replace(/inch/g, '"').replace(/\s+"/g, '"');

// The searchable text for a product: name, intro and every size's name/util/best.
const haystack = (p: Product) =>
  [
    p.title,
    p.subtitle,
    p.description,
    ...(p.specs ?? []).flatMap((s) => [s.label, s.util ?? '', s.best ?? '']),
  ]
    .join(' ')
    .toLowerCase();

const chipClass = (active: boolean) =>
  `min-h-11 rounded-full px-4 py-2 font-cta text-sm font-medium transition duration-200 active:scale-[0.97] ${
    active
      ? 'bg-primary text-primary-foreground shadow-sm'
      : 'border border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
  }`;

export default function ProductsInteractive({
  products,
  catalogueUrl,
}: {
  products: Product[];
  /** Uploaded main catalogue PDF; when present, drives the Download Catalogue button. */
  catalogueUrl?: string | null;
}) {
  const isHydrated = useIsHydrated();

  const [searchQuery, setSearchQuery] = useState('');
  const [material, setMaterial] = useState<MaterialFilter>(materialFromUrl);
  const [type, setType] = useState<TypeFilter>(typeFromUrl);
  const [selectedInquiryType, setSelectedInquiryType] = useState<string | null>(inquiryFromUrl);
  const [prefillProduct] = useState<Product | null>(() => productFromUrl(products));

  const inquiryRef = useRef<HTMLDivElement>(null);

  // Scroll to the inquiry form when we arrived via a ?product= / ?inquiry= deep
  // link or a #inquiry hash. The form only mounts after hydration.
  useEffect(() => {
    if (!isHydrated) return;
    const hashId = window.location.hash.slice(1);
    const targetId = prefillProduct || selectedInquiryType ? 'inquiry' : hashId;
    if (targetId) {
      setTimeout(() => document.getElementById(targetId)?.scrollIntoView({ block: 'start' }), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated]);

  const q = normaliseQuery(searchQuery);
  const filtered = products.filter((p) => {
    const matchMaterial = material === 'all' || p.material === material;
    const matchType = type === 'all' || p.type === type;
    const matchSearch = q === '' || haystack(p).includes(q);
    return matchMaterial && matchType && matchSearch;
  });

  const availableMaterials = MATERIAL_OPTIONS.filter((o) =>
    products.some((p) => p.material === o.value)
  );
  const availableTypes = TYPE_ORDER.filter((t) => products.some((p) => p.type === t));

  const totalSizes = filtered.reduce((n, p) => n + (p.specs?.length ?? p.sizes.length), 0);
  const anyActive = q !== '' || material !== 'all' || type !== 'all';

  const clearAll = () => {
    setSearchQuery('');
    setMaterial('all');
    setType('all');
  };

  const handleInquiryClick = (inqType: string) => {
    setSelectedInquiryType(inqType);
    setTimeout(
      () => inquiryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      150
    );
  };

  if (!isHydrated) {
    return <div className="min-h-screen bg-background pt-20" />;
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="mx-auto max-w-7xl">
          {/* Heading + enquiry CTAs */}
          <Reveal className="mb-8 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-2xl">
              <h1 className="font-headline text-3xl pt-6 font-bold text-foreground md:text-4xl lg:text-5xl">
                Our product range
              </h1>
              <p className="mt-3 font-body text-lg leading-relaxed text-muted-foreground">
                Sugarcane bagasse and areca palm leaf tableware for importers, distributors,
                wholesalers and caterers in the UK, EU and USA. Every size listed with its
                measurements and best use.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {[
                { type: 'Sample', label: 'Request Sample', icon: 'CubeIcon', primary: true },
                {
                  type: 'Catalogue',
                  label: 'Download Catalogue',
                  icon: 'ArrowDownTrayIcon',
                  primary: false,
                },
              ].map((cta) => (
                <button
                  key={cta.type}
                  onClick={() => handleInquiryClick(cta.type)}
                  className={`inline-flex min-h-12 items-center gap-2 whitespace-nowrap rounded-xl px-5 py-3 font-cta text-sm font-semibold transition duration-200 active:scale-[0.97] ${
                    cta.primary
                      ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                      : 'border border-border bg-surface text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={cta.icon} size={16} aria-hidden="true" />
                  {cta.label}
                </button>
              ))}
            </div>
          </Reveal>

          {/* Search + filters */}
          <Reveal className="mb-6">
            <div className="rounded-2xl border border-border bg-surface p-4 sm:p-5">
              {/* Search */}
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
                  <Icon name="MagnifyingGlassIcon" size={18} aria-hidden="true" />
                </span>
                <label htmlFor="product-search" className="sr-only">
                  Search products
                </label>
                <input
                  id="product-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={'Search by name, size or use, like 9 inch plate or soup'}
                  className="w-full rounded-xl border border-border bg-background py-3 pl-10 pr-9 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    className="absolute inset-y-0 right-2 flex items-center rounded-md px-1 text-muted-foreground transition hover:text-foreground"
                  >
                    <Icon name="XMarkIcon" size={16} aria-hidden="true" />
                  </button>
                )}
              </div>

              {/* Facets */}
              <div className="mt-4 flex flex-col gap-4 border-t border-border/60 pt-4 md:flex-row md:items-start md:gap-8">
                <div
                  role="group"
                  aria-label="Filter by material"
                  className="flex flex-wrap items-center gap-2"
                >
                  <span className="mr-1 font-cta text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Material
                  </span>
                  <button
                    type="button"
                    aria-pressed={material === 'all'}
                    onClick={() => setMaterial('all')}
                    className={chipClass(material === 'all')}
                  >
                    All
                  </button>
                  {availableMaterials.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={material === o.value}
                      onClick={() => setMaterial(o.value)}
                      className={chipClass(material === o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>

                <span
                  className="hidden h-6 w-px self-center bg-border md:block"
                  aria-hidden="true"
                />

                <div
                  role="group"
                  aria-label="Filter by product type"
                  className="flex flex-wrap items-center gap-2"
                >
                  <span className="mr-1 font-cta text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Type
                  </span>
                  <button
                    type="button"
                    aria-pressed={type === 'all'}
                    onClick={() => setType('all')}
                    className={chipClass(type === 'all')}
                  >
                    All
                  </button>
                  {availableTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={type === t}
                      onClick={() => setType(t)}
                      className={chipClass(type === t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Count + clear */}
          <div className="mb-6 flex items-center justify-between gap-3">
            <p className="font-cta text-sm text-muted-foreground">
              {filtered.length} product {filtered.length === 1 ? 'family' : 'families'} ·{' '}
              {totalSizes} {totalSizes === 1 ? 'size' : 'sizes'}
            </p>
            {anyActive && (
              <button
                type="button"
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-cta text-sm font-medium text-primary transition hover:bg-primary/10 active:scale-[0.97]"
              >
                <Icon name="XMarkIcon" size={14} aria-hidden="true" />
                Clear filters
              </button>
            )}
          </div>

          {/* Grid */}
          <h2 className="sr-only">Product catalogue</h2>
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {filtered.map((product, i) => (
                <Reveal as="div" key={product.id} delay={(i % 3) * 70} className="h-full">
                  <ProductCard product={product} priority={i < 3} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-surface py-16 text-center">
              <Icon
                name="MagnifyingGlassIcon"
                size={40}
                className="mx-auto mb-4 text-muted-foreground/50"
                aria-hidden="true"
              />
              <p className="font-body text-lg text-muted-foreground">
                No products match those filters.
              </p>
              {anyActive && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-4 py-2 font-cta text-sm font-medium text-foreground transition hover:bg-muted active:scale-[0.97]"
                >
                  <Icon name="XMarkIcon" size={16} aria-hidden="true" />
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Enquiry form */}
      <div ref={inquiryRef}>
        <InquiryForm
          selectedInquiryType={selectedInquiryType}
          products={products}
          catalogueUrl={catalogueUrl}
          prefillProduct={prefillProduct}
        />
      </div>
    </div>
  );
}
